<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ $t('app.tabs.leaderboard') }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="handleRefresh" data-cy="refresh-button">
            <ion-icon :icon="refreshOutline" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <ion-refresher slot="fixed" @ionRefresh="refreshData($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>

      <template v-if="isLoading">
        <div class="bg-background-secondary py-lg px-md mb-md" data-cy="skeleton-podium">
          <div class="flex justify-center items-end gap-md max-w-[600px] mx-auto">
            <!-- Second Place Skeleton -->
            <div class="flex flex-col items-center text-center">
              <div class="w-[60px] h-[60px] border-[3px] border-silver rounded-full mb-xs overflow-hidden" data-cy="skeleton-second-place">
                <ion-skeleton-text class="w-full h-full bg-silver/50" :animated="true"></ion-skeleton-text>
              </div>
              <div class="w-full">
                <ion-skeleton-text class="w-[16px] h-[24px] mx-auto" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[75px] h-[16px] mx-auto my-0.5" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[50px] h-[12px] mx-auto" :animated="true"></ion-skeleton-text>
              </div>
            </div>

            <!-- First Place Skeleton -->
            <div class="flex flex-col items-center text-center">
              <div class="w-[80px] h-[80px] border-[3px] border-gold rounded-full mb-xs overflow-hidden" data-cy="skeleton-first-place">
                <ion-skeleton-text class="w-full h-full bg-gold/50" :animated="true"></ion-skeleton-text>
              </div>
              <div class="w-full">
                <ion-skeleton-text class="w-[16px] h-[24px] mx-auto" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[75px] h-[16px] mx-auto my-0.5" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[50px] h-[12px] mx-auto" :animated="true"></ion-skeleton-text>
              </div>
            </div>

            <!-- Third Place Skeleton -->
            <div class="flex flex-col items-center text-center">
              <div class="w-[60px] h-[60px] border-[3px] border-bronze rounded-full mb-xs overflow-hidden" data-cy="skeleton-third-place">
                <ion-skeleton-text class="w-full h-full bg-bronze/50" :animated="true"></ion-skeleton-text>
              </div>
              <div class="w-full">
                <ion-skeleton-text class="w-[16px] h-[24px] mx-auto" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[75px] h-[16px] mx-auto my-0.5" :animated="true"></ion-skeleton-text>
                <ion-skeleton-text class="w-[50px] h-[12px] mx-auto" :animated="true"></ion-skeleton-text>
              </div>
            </div>
          </div>
        </div>
        
        <div class="px-sm max-w-[600px] mx-auto" data-cy="skeleton-rankings">
          <ion-list class="p-sm md:p-lg">
            <!-- Showing only 3 skeleton items to match the focused view -->
            <ion-item v-for="n in 3" :key="n" 
              class="rounded-sm"
              :style="n === 2 ? 
                '--padding-start: 20px; --padding-end: 20px; --padding-top: 16px; --padding-bottom: 16px; margin: 16px 0; background-color: white; border-left: 4px solid var(--ion-color-primary); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);' : 
                '--padding-start: 16px; --padding-end: 16px; --padding-top: 12px; --padding-bottom: 12px; margin: 8px 0; background-color: white; opacity: 0.5;'"
              data-cy="skeleton-rank-item">
              <div class="w-[24px] h-[24px] rounded-sm mr-md flex items-center justify-center bg-background-secondary">
                <ion-skeleton-text class="w-full h-full"></ion-skeleton-text>
              </div>
              <ion-label>
                <div class="w-[65%] h-[18px] mb-md mt-md">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
                <div class="w-[45%] h-[14px] mb-md">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
              </ion-label>
              <div slot="end" class="w-[70px] h-[24px] rounded-sm overflow-hidden mr-1">
                <ion-skeleton-text class="w-full h-full rounded-sm bg-primary/50" :animated="true"></ion-skeleton-text>
              </div>
            </ion-item>
          </ion-list>
        </div>
      </template>

      <template v-else>
        <!-- Podium Section (Top 3) -->
        <div class="bg-background-secondary py-lg px-md mb-md" data-cy="podium-section">
          <div class="flex justify-center items-end gap-md max-w-[600px] mx-auto">
            <!-- Second Place -->
            <div class="flex flex-col items-center text-center animate-scale-in" v-if="topThree && topThree.length > 1" data-cy="second-place">
              <div class="w-[60px] h-[60px] mb-xs rounded-full flex items-center justify-center bg-silver border-[3px] border-silver">
                <ion-icon :icon="medalOutline" class="text-4xl text-white"></ion-icon>
              </div>
              <div class="w-full">
                <div class="text-xl font-bold text-primary" data-cy="podium-rank">2</div>
                <div class="text-sm font-semibold text-text-primary my-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px] md:max-w-[150px]">
                  {{ topThree[1].display_name }}
                </div>
                <div class="text-xs text-text-secondary">{{ topThree[1].score }} {{ $t('leaderboard.pts') }}</div>
              </div>
            </div>

            <!-- First Place -->
            <div class="flex flex-col items-center text-center animate-scale-in" v-if="topThree && topThree.length > 0" data-cy="first-place">
              <div class="w-[80px] h-[80px] mb-xs rounded-full flex items-center justify-center bg-gold border-[3px] border-gold">
                <ion-icon :icon="trophyOutline" class="text-5xl text-white"></ion-icon>
              </div>
              <div class="w-full">
                <div class="text-xl font-bold text-primary" data-cy="podium-rank">1</div>
                <div class="text-sm font-semibold text-text-primary my-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px] md:max-w-[150px]">
                  {{ topThree[0].display_name }}
                </div>
                <div class="text-xs text-text-secondary">{{ topThree[0].score }} {{ $t('leaderboard.pts') }}</div>
              </div>
            </div>

            <!-- Third Place -->
            <div class="flex flex-col items-center text-center animate-scale-in" v-if="topThree && topThree.length > 2" data-cy="third-place">
              <div class="w-[60px] h-[60px] mb-xs rounded-full flex items-center justify-center bg-bronze border-[3px] border-bronze">
                <ion-icon :icon="medalOutline" class="text-4xl text-white"></ion-icon>
              </div>
              <div class="w-full">
                <div class="text-xl font-bold text-primary" data-cy="podium-rank">3</div>
                <div class="text-sm font-semibold text-text-primary my-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px] md:max-w-[150px]">
                  {{ topThree[2].display_name }}
                </div>
                <div class="text-xs text-text-secondary">{{ topThree[2].score }} {{ $t('leaderboard.pts') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Focused View Section (Current User + Adjacent) -->
        <div class="mx-auto px-sm md:max-w-[600px] md:px-lg">
          <ion-list class="p-sm md:p-lg" data-cy="focused-rankings-list">
            <ion-item
              v-for="entry in focusedViewEntries"
              :key="entry.customer_id"
              class="rounded-sm"
              :style="entry.customer_id === currentUserId ? 
                '--padding-start: 20px; --padding-end: 20px; --padding-top: 16px; --padding-bottom: 16px; margin: 16px 0; background-color: white; border-left: 4px solid var(--ion-color-primary); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);' : 
                '--padding-start: var(--space-md); --padding-end: var(--space-md); --padding-top: var(--space-sm); --padding-bottom: var(--space-sm); margin: 8px 0; background-color: white; opacity: 0.5;'"
              data-cy="focused-rank-item"
              :data-cy-current-user="entry.customer_id === currentUserId ? 'true' : null"
            >
              <div 
                class="flex items-center justify-center w-[32px] h-[24px] rounded-sm font-semibold text-sm mr-md" 
                :class="{
                  'bg-background-secondary text-text-primary': !getRankClass(entry.rank),
                  'bg-gold text-black': getRankClass(entry.rank) === 'gold',
                  'bg-silver text-black': getRankClass(entry.rank) === 'silver',
                  'bg-bronze text-black': getRankClass(entry.rank) === 'bronze'
                }"
                data-cy="rank-badge"
              >
                {{ entry.rank }}
              </div>
              
              <ion-label>
                <h2 class="text-base font-semibold text-text-primary mb-xs" 
                   :class="{ 'font-bold': entry.customer_id === currentUserId }"
                   data-cy="user-name">
                  {{ entry.display_name }}
                  <ion-icon v-if="entry.customer_id === currentUserId" :icon="personCircleOutline" class="ml-1 text-primary"></ion-icon>
                </h2>
                <p class="text-sm text-text-secondary" data-cy="user-membership">
                  {{ getLocalizedMembershipType(entry.customer_id) }}
                </p>
              </ion-label>

              <ion-badge slot="end" 
                :class="{ 'px-sm py-xs font-semibold': true, 'bg-primary-dark': entry.customer_id === currentUserId }"
                data-cy="points-badge">
                {{ entry.score }} {{ $t('leaderboard.pts') }}
              </ion-badge>
            </ion-item>
          </ion-list>
        </div>
      </template>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { onMounted, defineExpose } from 'vue'
import { useLeaderboardData } from '../composables/useLeaderboardData'
import { useI18n } from 'vue-i18n'
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonList, IonItem, IonLabel, IonBadge,
  IonIcon, IonButton, IonButtons,
  IonRefresher, IonRefresherContent, IonSkeletonText
} from '@ionic/vue'
import {
  trophyOutline, personCircleOutline, refreshOutline,
  medalOutline
} from 'ionicons/icons'

const i18n = useI18n()

const {
  isLoading,
  currentUserId,
  topThree,
  focusedViewEntries,
  loadData,
  getMembershipType,
  getRankClass
} = useLeaderboardData()

// Function to get localized membership type
function getLocalizedMembershipType(customerId: string): string {
  const membershipType = getMembershipType(customerId)
  return i18n.t(`tiers.${membershipType.toLowerCase()}`)
}

onMounted(async () => {
  // Start loading data immediately when component mounts
  loadData()
})

async function refreshData(event: CustomEvent) {
  await loadData()
  
  if (event && event.target) {
    // Type assertion for Ionic refresher element
    const refresher = event.target as HTMLIonRefresherElement
    if (refresher.complete) {
      refresher.complete()
    }
  }
}

function handleRefresh() {
  loadData()
}

// Expose methods for testing
defineExpose({
  loadData
})
</script>

<style scoped>
/* Custom keyframes for animations that can't be easily achieved with Tailwind */
@keyframes slideInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slideDown {
  animation: slideDown var(--transition-base);
}

@keyframes scale-in {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scale-in {
  animation: scale-in 0.3s ease-out forwards;
}
</style> 